import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel';

// ==============================
// 型定義
// ==============================

interface EvaluationPeriod {
  period_id: string;
  period_name: string;
  company_adjustment_rate: number;
  status: 'draft' | 'active' | 'closed';
}

interface Employee {
  employee_id: string;
  name: string;
  email: string;
  department_id?: string;
  department_name?: string;
  job_grade?: string;
  job_title?: string;
}

interface Evaluator {
  employee: Employee;
  weight: number;
}

interface EvaluationRoute {
  id: number;
  period_id: string;
  employee_id: string;
  employee_name: string;
  evaluator_id: string;
  evaluator_name: string;
  evaluation_category: 'self' | 'manager' | 'dept_head';
  evaluation_weight: number;
  applies_to_manager_eval: boolean;
}

interface RouteGroup {
  employee_id: string;
  employee_name: string;
  department_name?: string;
  self_routes: EvaluationRoute[];
  manager_routes: EvaluationRoute[];
  dept_head_routes: EvaluationRoute[];
  has_dual_role_dept_head: boolean;
}

// ==============================
// メインコンポーネント
// ==============================

const EvaluationRouteManagementPage: React.FC = () => {
  // ステート管理
  const [periods, setPeriods] = useState<EvaluationPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [routes, setRoutes] = useState<RouteGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 検索機能
  const [searchQuery, setSearchQuery] = useState('');

  // ダイアログの状態
  const [individualDialogOpen, setIndividualDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  // 個別設定フォームの状態
  const [targetEmployee, setTargetEmployee] = useState<Employee | null>(null);
  const [managerEvaluators, setManagerEvaluators] = useState<Evaluator[]>([]);
  const [deptEvaluators, setDeptEvaluators] = useState<Evaluator[]>([]);
  const [disableManagerEval, setDisableManagerEval] = useState(false);

  // 一時的な評価者追加用
  const [tempManagerEvaluator, setTempManagerEvaluator] = useState<Employee | null>(null);
  const [tempManagerWeight, setTempManagerWeight] = useState<number>(1.0);
  const [tempDeptEvaluator, setTempDeptEvaluator] = useState<Employee | null>(null);
  const [tempDeptWeight, setTempDeptWeight] = useState<number>(1.0);

  // 一括作成フォームの状態
  const [bulkTargetEmployees, setBulkTargetEmployees] = useState<Employee[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [importErrors, setImportErrors] = useState<{ row: number; employee_id: string; message: string }[]>([]);

  // プレビュー機能の状態
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);

  // 削除機能の状態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetRoute, setDeleteTargetRoute] = useState<EvaluationRoute | null>(null);
  const [deletionImpact, setDeletionImpact] = useState<any>(null);
  const [checkingDeletion, setCheckingDeletion] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ==============================
  // データ取得
  // ==============================

  useEffect(() => {
    fetchPeriods();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchRoutes();
    }
  }, [selectedPeriod]);

  const fetchPeriods = async () => {
    try {
      const response = await api.get('/evaluation-periods');

      if (response.data.success) {
        const periodsData = response.data.data || [];
        setPeriods(periodsData);

        // アクティブ期間がある場合は自動選択、なければ最新のドラフトを選択
        if (!selectedPeriod && periodsData.length > 0) {
          const activePeriod = periodsData.find((p: EvaluationPeriod) => p.status === 'active');
          if (activePeriod) {
            setSelectedPeriod(activePeriod.period_id);
          } else {
            // アクティブがなければドラフト、それもなければ最新
            const draftPeriod = periodsData.find((p: EvaluationPeriod) => p.status === 'draft');
            setSelectedPeriod(draftPeriod ? draftPeriod.period_id : periodsData[0].period_id);
          }
        }
      }
    } catch (err) {
      console.error('評価期間取得エラー:', err);
      toast.error(extractErrorMessage(err, '評価期間の取得に失敗しました'));
    }
  };

  const fetchEmployees = async () => {
    try {
      // limit制限のないsimple APIを使用して全従業員を取得
      const response = await api.get('/employees/simple');

      const data = response.data.data;

      if (Array.isArray(data)) {
        setEmployees(data);
      } else {
        console.error('従業員データが配列ではありません:', data);
        setEmployees([]);
        setError('従業員データの形式が不正です');
      }
    } catch (err) {
      console.error('従業員データ取得エラー:', err);
      setEmployees([]);
      setError(extractErrorMessage(err, '従業員データの取得に失敗しました'));
    }
  };

  const fetchRoutes = async () => {
    if (!selectedPeriod) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/evaluation-routes', {
        params: { period_id: selectedPeriod }
      });

      if (response.data.success) {
        const routesData: EvaluationRoute[] = response.data.data || [];

        // 従業員ごとにグループ化
        const grouped = groupRoutesByEmployee(routesData);
        setRoutes(grouped);
      }
    } catch (err) {
      console.error('評価ルート取得エラー:', err);
      setError(extractErrorMessage(err, '評価ルートの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const groupRoutesByEmployee = (routesData: EvaluationRoute[]): RouteGroup[] => {
    const employeeMap = new Map<string, RouteGroup>();

    routesData.forEach(route => {
      if (!employeeMap.has(route.employee_id)) {
        employeeMap.set(route.employee_id, {
          employee_id: route.employee_id,
          employee_name: route.employee_name,
          department_name: route.employee_id, // TODO: 部門名を取得
          self_routes: [],
          manager_routes: [],
          dept_head_routes: [],
          has_dual_role_dept_head: false
        });
      }

      const group = employeeMap.get(route.employee_id)!;

      if (route.evaluation_category === 'self') {
        group.self_routes.push(route);
      } else if (route.evaluation_category === 'manager') {
        group.manager_routes.push(route);
      } else if (route.evaluation_category === 'dept_head') {
        group.dept_head_routes.push(route);
        if (route.applies_to_manager_eval) {
          group.has_dual_role_dept_head = true;
        }
      }
    });

    return Array.from(employeeMap.values());
  };

  // 検索フィルタリング
  const filteredRoutes = useMemo(() => {
    if (!searchQuery.trim()) return routes;
    const query = searchQuery.toLowerCase();
    return routes.filter(
      (group) =>
        group.employee_id?.toLowerCase().includes(query) ||
        group.employee_name?.toLowerCase().includes(query) ||
        group.department_name?.toLowerCase().includes(query)
    );
  }, [routes, searchQuery]);

  // ==============================
  // 個別設定ダイアログ
  // ==============================

  const handleOpenIndividualDialog = () => {
    // 初期化
    setTargetEmployee(null);
    setManagerEvaluators([]);
    setDeptEvaluators([]);
    setDisableManagerEval(false);
    setTempManagerEvaluator(null);
    setTempManagerWeight(1.0);
    setTempDeptEvaluator(null);
    setTempDeptWeight(1.0);
    setIndividualDialogOpen(true);
  };

  const handleEditEmployee = async (employeeId: string) => {
    if (!selectedPeriod) {
      toast.error('評価期間を選択してください');
      return;
    }

    try {
      // 従業員情報を取得
      const employee = employees.find(e => e.employee_id === employeeId);
      if (!employee) {
        toast.error('従業員情報が見つかりません');
        return;
      }

      // 編集可否と変更による影響のチェック
      const checkEditResponse = await api.get(
        `/evaluation-routes/employee/${employeeId}/period/${selectedPeriod}/check-edit`
      );

      if (checkEditResponse.data.success) {
        const check = checkEditResponse.data.data;

        if (!check.can_edit) {
          toast.error(check.warning_message || '評価データが存在するため編集できません。', {
            autoClose: 5000
          });
          return;
        }

        // 保存は差分適用のため目標・自己評価・提出済み評価は失われないが、
        // 提出済みの評価者を外すと集計対象から外れるため事前に知らせる
        if (check.warning_message) {
          toast.warning(check.warning_message, { autoClose: 8000 });
        }
      }

      // 既存の評価ルートを取得
      const response = await api.get(
        `/evaluation-routes/employee/${employeeId}/period/${selectedPeriod}`
      );

      if (response.data.success) {
        const existingRoutes: EvaluationRoute[] = response.data.data || [];

        // ルートをカテゴリ別に分類
        const managerRoutes = existingRoutes.filter(r => r.evaluation_category === 'manager');
        const deptRoutes = existingRoutes.filter(r => r.evaluation_category === 'dept_head');
        const hasDisabledManager = deptRoutes.some(r => r.applies_to_manager_eval);

        // 評価者情報を構築
        const managerEvals: Evaluator[] = managerRoutes.map(r => {
          const evaluator = employees.find(e => e.employee_id === r.evaluator_id);
          return {
            employee: evaluator || { employee_id: r.evaluator_id, name: r.evaluator_name, email: '' },
            weight: Number(r.evaluation_weight)
          };
        });

        const deptEvals: Evaluator[] = deptRoutes.map(r => {
          const evaluator = employees.find(e => e.employee_id === r.evaluator_id);
          return {
            employee: evaluator || { employee_id: r.evaluator_id, name: r.evaluator_name, email: '' },
            weight: Number(r.evaluation_weight)
          };
        });

        // フォームに設定
        setTargetEmployee(employee);
        setManagerEvaluators(managerEvals);
        setDeptEvaluators(deptEvals);
        setDisableManagerEval(hasDisabledManager);
        setTempManagerEvaluator(null);
        setTempManagerWeight(1.0);
        setTempDeptEvaluator(null);
        setTempDeptWeight(1.0);
        setIndividualDialogOpen(true);
      }
    } catch (error) {
      console.error('既存ルート取得エラー:', error);
      toast.error(extractErrorMessage(error, '既存ルートの取得に失敗しました'));
    }
  };

  const handleCloseIndividualDialog = () => {
    setIndividualDialogOpen(false);
  };

  const handleAddManagerEvaluator = () => {
    if (!tempManagerEvaluator) {
      toast.error('評価者を選択してください');
      return;
    }

    if (tempManagerWeight <= 0 || tempManagerWeight > 1) {
      toast.error('重みは0より大きく1以下で入力してください');
      return;
    }

    // 自分自身をチェック
    if (targetEmployee && tempManagerEvaluator.employee_id === targetEmployee.employee_id) {
      toast.error('上長評価では自分自身を評価者として設定できません');
      return;
    }

    // 重複チェック
    if (managerEvaluators.some(e => e.employee.employee_id === tempManagerEvaluator.employee_id)) {
      toast.error('この評価者は既に追加されています');
      return;
    }

    setManagerEvaluators([...managerEvaluators, {
      employee: tempManagerEvaluator,
      weight: tempManagerWeight
    }]);

    setTempManagerEvaluator(null);
    setTempManagerWeight(1.0);
  };

  const handleRemoveManagerEvaluator = (index: number) => {
    setManagerEvaluators(managerEvaluators.filter((_, i) => i !== index));
  };

  const handleAddDeptEvaluator = () => {
    // 部門長評価は1人のみに制限
    if (deptEvaluators.length >= 1) {
      toast.error('部門長評価は1人のみ指定できます。定量評価のため、複数人の指定はできません。');
      return;
    }

    if (!tempDeptEvaluator) {
      toast.error('評価者を選択してください');
      return;
    }

    if (tempDeptWeight <= 0 || tempDeptWeight > 1) {
      toast.error('重みは0より大きく1以下で入力してください');
      return;
    }

    // 自分自身をチェック
    if (targetEmployee && tempDeptEvaluator.employee_id === targetEmployee.employee_id) {
      toast.error('部門長評価では自分自身を評価者として設定できません');
      return;
    }

    // 重複チェック
    if (deptEvaluators.some(e => e.employee.employee_id === tempDeptEvaluator.employee_id)) {
      toast.error('この評価者は既に追加されています');
      return;
    }

    // 部門長評価は1人のみなので、重みは自動的に100%
    setDeptEvaluators([...deptEvaluators, {
      employee: tempDeptEvaluator,
      weight: 1.0
    }]);

    setTempDeptEvaluator(null);
    setTempDeptWeight(1.0);
  };

  const handleRemoveDeptEvaluator = (index: number) => {
    setDeptEvaluators(deptEvaluators.filter((_, i) => i !== index));
  };

  const calculateTotalWeight = (evaluators: Evaluator[]): number => {
    return evaluators.reduce((sum, e) => sum + e.weight, 0);
  };

  // ==============================
  // データエクスポート（CSV）
  // ==============================

  const handleExportData = () => {
    // CSVヘッダー
    const headers = [
      'period_id',
      'employee',
      'manager_evaluator_1',
      'manager_evaluator_1_weight',
      'manager_evaluator_2',
      'manager_evaluator_2_weight',
      'manager_evaluator_3',
      'manager_evaluator_3_weight',
      'dept_evaluator_1',
      'dept_evaluator_1_weight',
      'dept_evaluator_2',
      'dept_evaluator_2_weight',
      'dept_evaluator_3',
      'dept_evaluator_3_weight',
      'disable_manager_eval'
    ];

    // 現在のデータからエクスポート
    const dataRows: string[][] = routes.map((group) => {
      // 上長評価者を取得（最大3人）
      const managers = group.manager_routes.slice(0, 3);
      const mgr1 = managers[0];
      const mgr2 = managers[1];
      const mgr3 = managers[2];

      // 部門長評価者を取得（最大3人）
      const deptHeads = group.dept_head_routes.slice(0, 3);
      const dept1 = deptHeads[0];
      const dept2 = deptHeads[1];
      const dept3 = deptHeads[2];

      return [
        selectedPeriod,
        `${group.employee_id} (${group.employee_name})`,
        mgr1 ? `${mgr1.evaluator_id} (${mgr1.evaluator_name})` : '',
        mgr1 ? String(mgr1.evaluation_weight) : '',
        mgr2 ? `${mgr2.evaluator_id} (${mgr2.evaluator_name})` : '',
        mgr2 ? String(mgr2.evaluation_weight) : '',
        mgr3 ? `${mgr3.evaluator_id} (${mgr3.evaluator_name})` : '',
        mgr3 ? String(mgr3.evaluation_weight) : '',
        dept1 ? `${dept1.evaluator_id} (${dept1.evaluator_name})` : '',
        dept1 ? String(dept1.evaluation_weight) : '',
        dept2 ? `${dept2.evaluator_id} (${dept2.evaluator_name})` : '',
        dept2 ? String(dept2.evaluation_weight) : '',
        dept3 ? `${dept3.evaluator_id} (${dept3.evaluator_name})` : '',
        dept3 ? String(dept3.evaluation_weight) : '',
        group.has_dual_role_dept_head ? 'true' : 'false'
      ];
    });

    // CSV生成
    const csvContent = [
      headers.join(','),
      ...dataRows.map(row => row.join(','))
    ].join('\n');

    // BOM付きでダウンロード（Excel対応）
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `evaluation_routes_export_${selectedPeriod || 'all'}_${(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`${dataRows.length}件の評価ルートデータをエクスポートしました`);
  };

  // ==============================
  // CSVインポート
  // ==============================

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('CSVファイルを選択してください');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleCsvPreview = async () => {
    if (!csvFile) {
      toast.error('CSVファイルを選択してください');
      return;
    }

    setPreviewing(true);

    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      const response = await api.post(
        '/evaluation-routes/preview-csv',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setPreviewData(response.data.data);
        setPreviewDialogOpen(true);
      }
    } catch (err) {
      console.error('CSVプレビューエラー:', err);
      toast.error(extractErrorMessage(err, 'CSVプレビューに失敗しました'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('CSVファイルを選択してください');
      return;
    }

    if (!selectedPeriod) {
      toast.error('評価期間を選択してください');
      return;
    }

    setCsvUploading(true);
    setImportErrors([]); // エラーをリセット

    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      const response = await api.post(
        '/evaluation-routes/import-csv',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const { created_count, error_count, errors } = response.data.data;

        if (error_count > 0) {
          // エラーを状態に保存してUIに表示
          const formattedErrors = (errors || []).map((err: any, index: number) => ({
            row: err.row || index + 2, // ヘッダー行 + 1
            employee_id: err.employee_id || '-',
            message: err.message || err.error || 'エラー'
          }));
          setImportErrors(formattedErrors);
          toast.warning(
            `${created_count}件作成、${error_count}件エラー。下記のエラー詳細を確認してください。`,
            { autoClose: 5000 }
          );
        } else {
          toast.success(`${created_count}件の評価ルートを作成しました`);
          setBulkDialogOpen(false);
          setCsvFile(null);
        }

        // データを再取得
        await fetchRoutes();
        setPreviewDialogOpen(false);
        setPreviewData(null);
      }
    } catch (err) {
      console.error('CSVアップロードエラー:', err);
      toast.error(extractErrorMessage(err, 'CSVアップロードに失敗しました'));
    } finally {
      setCsvUploading(false);
    }
  };

  const handleSubmitIndividualRoute = async () => {
    // バリデーション
    if (!targetEmployee) {
      toast.error('評価対象者を選択してください');
      return;
    }

    if (!selectedPeriod) {
      toast.error('評価期間を選択してください');
      return;
    }

    // 上長評価が無効でない場合、上長評価者が必要
    if (!disableManagerEval && managerEvaluators.length === 0) {
      toast.error('上長評価者を少なくとも1人追加してください');
      return;
    }

    // 部門評価者が必要
    if (deptEvaluators.length === 0) {
      toast.error('部門長評価者を少なくとも1人追加してください');
      return;
    }

    // 重みの合計チェック
    if (!disableManagerEval) {
      const managerTotal = calculateTotalWeight(managerEvaluators);
      if (Math.abs(managerTotal - 1.0) > 0.001) {
        toast.error(`上長評価者の重み合計が100%になっていません: ${Number(managerTotal * 100).toFixed(1)}%`);
        return;
      }
    }

    const deptTotal = calculateTotalWeight(deptEvaluators);
    if (Math.abs(deptTotal - 1.0) > 0.001) {
      toast.error(`部門長評価者の重み合計が100%になっていません: ${Number(deptTotal * 100).toFixed(1)}%`);
      return;
    }

    try {
      // 評価ルートを作成
      const routesToCreate = [];

      // 自己評価（自動生成）
      routesToCreate.push({
        period_id: selectedPeriod,
        employee_id: targetEmployee.employee_id,
        evaluator_id: targetEmployee.employee_id,
        evaluation_category: 'self',
        evaluation_weight: 1.0,
        applies_to_manager_eval: false
      });

      // 上長評価
      if (!disableManagerEval) {
        for (const evaluator of managerEvaluators) {
          routesToCreate.push({
            period_id: selectedPeriod,
            employee_id: targetEmployee.employee_id,
            evaluator_id: evaluator.employee.employee_id,
            evaluation_category: 'manager',
            evaluation_weight: evaluator.weight,
            applies_to_manager_eval: false
          });
        }
      }

      // 部門評価
      for (const evaluator of deptEvaluators) {
        routesToCreate.push({
          period_id: selectedPeriod,
          employee_id: targetEmployee.employee_id,
          evaluator_id: evaluator.employee.employee_id,
          evaluation_category: 'dept_head',
          evaluation_weight: evaluator.weight,
          applies_to_manager_eval: disableManagerEval // 上長評価無効時はフラグをtrue
        });
      }

      // 一括送信
      const response = await api.post(
        '/evaluation-routes/bulk',
        { routes: routesToCreate }
      );

      if (response.data.success) {
        const result = response.data.data || {};

        // 一部の項目が適用されなかった場合は成功として扱わない
        if ((result.error_count || 0) > 0) {
          const firstError = result.errors?.[0]?.error;
          toast.error(firstError || '評価ルートを保存できなかった項目があります', {
            autoClose: 8000
          });
          return;
        }

        toast.success(response.data.message || '評価ルートを保存しました');
        fetchRoutes();
        handleCloseIndividualDialog();
      }
    } catch (err) {
      console.error('評価ルート保存エラー:', err);
      toast.error(extractErrorMessage(err, '評価ルートの保存に失敗しました'));
    }
  };

  // ==============================
  // 一括作成ダイアログ（後で実装）
  // ==============================

  const handleOpenBulkDialog = () => {
    setBulkTargetEmployees([]);
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => {
    setBulkDialogOpen(false);
  };

  // ==============================
  // 削除処理
  // ==============================

  const handleDeleteRoute = async (employeeId: string, employeeName: string) => {
    if (!selectedPeriod) {
      toast.error('評価期間が選択されていません');
      return;
    }

    // 削除対象の従業員情報を一時保存
    setDeleteTargetRoute({ employee_id: employeeId, employee_name: employeeName } as any);
    setCheckingDeletion(true);
    setDeleteDialogOpen(true);

    try {
      const response = await api.get(
        `/evaluation-routes/employee/${employeeId}/period/${selectedPeriod}/check-deletion`
      );

      if (response.data.success) {
        setDeletionImpact(response.data.data);
      }
    } catch (error) {
      console.error('削除影響チェックエラー:', error);
      toast.error(extractErrorMessage(error, '削除影響チェックに失敗しました'));
      setDeleteDialogOpen(false);
    } finally {
      setCheckingDeletion(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetRoute || !selectedPeriod) return;

    setDeleting(true);

    try {
      const response = await api.delete(
        `/evaluation-routes/employee/${deleteTargetRoute.employee_id}/period/${selectedPeriod}`
      );

      if (response.data.success) {
        const { deleted_routes_count, deleted_history_count, deleted_items_count } = response.data.data;
        toast.success(`評価ルートと関連データを削除しました（ルート: ${deleted_routes_count}件、評価履歴: ${deleted_history_count}件、評価項目: ${deleted_items_count}件）`);
      }

      await fetchRoutes();
      setDeleteDialogOpen(false);
      setDeleteTargetRoute(null);
      setDeletionImpact(null);
    } catch (error) {
      console.error('削除エラー:', error);
      toast.error(extractErrorMessage(error, '削除に失敗しました'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetRoute(null);
    setDeletionImpact(null);
  };

  // ==============================
  // レンダリング
  // ==============================

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          評価ルート設定
        </Typography>
        <Typography variant="body2" color="text.secondary">
          評価対象者と評価者のルート設定を管理します
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ※ 評価ルートを設定すると、評価期間中の評価フローが確定します
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 統合ツールバー: 評価期間・検索・アクション */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* 評価期間選択 */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>評価期間</InputLabel>
            <Select
              value={selectedPeriod}
              label="評価期間"
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              {periods.map(period => (
                <MenuItem key={period.period_id} value={period.period_id}>
                  {period.period_name} ({period.status === 'active' ? '有効' : period.status === 'draft' ? '下書き' : '終了'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 全社統一調整率 */}
          {selectedPeriod && (
            <Chip
              icon={<InfoIcon />}
              label={`全社統一調整率: ${Number(periods.find(p => p.period_id === selectedPeriod)?.company_adjustment_rate || 0).toFixed(2)}%`}
              color="info"
              variant="outlined"
            />
          )}

          {/* 検索 */}
          <TextField
            size="small"
            placeholder="社員ID、氏名、部署で検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250, flex: 1 }}
          />

          {/* アクションボタン */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportData}
              disabled={!selectedPeriod}
            >
              データエクスポート
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={handleOpenBulkDialog}
              disabled={!selectedPeriod}
            >
              一括登録
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchRoutes}
              disabled={!selectedPeriod}
            >
              更新
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenIndividualDialog}
              disabled={!selectedPeriod}
            >
              個別設定
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 評価ルート一覧 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredRoutes.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              {!selectedPeriod
                ? '評価期間を選択してください'
                : searchQuery
                ? '検索条件に一致する評価ルートがありません'
                : '評価ルートが設定されていません'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {filteredRoutes.map((group) => (
            <Accordion key={group.employee_id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6">{group.employee_name}</Typography>
                  <Chip label={group.employee_id} size="small" variant="outlined" />
                  {group.department_name && (
                    <Chip label={group.department_name} size="small" color="primary" variant="outlined" />
                  )}
                  {group.has_dual_role_dept_head && (
                    <Chip label="上長評価無効" size="small" color="warning" />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="個別編集">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEmployee(group.employee_id);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="評価ルート削除">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRoute(group.employee_id, group.employee_name);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* 自己評価 */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      自己評価
                    </Typography>
                    {group.self_routes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">未設定</Typography>
                    ) : (
                      <List dense>
                        {group.self_routes.map(route => (
                          <ListItem key={route.id}>
                            <ListItemText
                              primary={route.evaluator_name}
                              secondary={`重み: ${Number(route.evaluation_weight * 100).toFixed(0)}%`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>

                  {/* 上長評価 */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      上長評価 {group.has_dual_role_dept_head && <Chip label="無効" size="small" color="warning" />}
                    </Typography>
                    {group.manager_routes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {group.has_dual_role_dept_head ? '部門長評価が適用' : '未設定'}
                      </Typography>
                    ) : (
                      <List dense>
                        {group.manager_routes.map(route => (
                          <ListItem key={route.id}>
                            <ListItemText
                              primary={route.evaluator_name}
                              secondary={`重み: ${Number(route.evaluation_weight * 100).toFixed(0)}%`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>

                  {/* 部門評価 */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      部門長評価
                    </Typography>
                    {group.dept_head_routes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">未設定</Typography>
                    ) : (
                      <List dense>
                        {group.dept_head_routes.map(route => (
                          <ListItem key={route.id}>
                            <ListItemText
                              primary={route.evaluator_name}
                              secondary={`重み: ${Number(route.evaluation_weight * 100).toFixed(0)}%`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* ==============================
          個別設定ダイアログ
          ============================== */}
      <Dialog open={individualDialogOpen} onClose={handleCloseIndividualDialog} maxWidth="md" fullWidth>
        <DialogTitle>評価ルート個別設定</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* 評価対象者 */}
            <RequiredFieldLabel label="評価対象者" />
            <Autocomplete
              options={employees || []}
              getOptionLabel={(option) => `${option.name} (${option.employee_id})`}
              value={targetEmployee}
              onChange={(_, newValue) => setTargetEmployee(newValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="評価対象者を選択してください" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.employee_id === value?.employee_id}
            />

            <Divider sx={{ my: 3 }} />

            {/* 上長評価者セクション */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">上長評価者</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={disableManagerEval}
                      onChange={(e) => setDisableManagerEval(e.target.checked)}
                    />
                  }
                  label="上長評価を無効にする"
                />
              </Box>

              {!disableManagerEval && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    複数の評価者を指名できます。重みの合計が100%になるようにしてください。
                  </Alert>

                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={7}>
                      <Autocomplete
                        options={employees || []}
                        getOptionLabel={(option) => `${option.name} (${option.employee_id})`}
                        value={tempManagerEvaluator}
                        onChange={(_, newValue) => setTempManagerEvaluator(newValue)}
                        renderInput={(params) => (
                          <TextField {...params} label="評価者を選択" margin="none" />
                        )}
                        isOptionEqualToValue={(option, value) => option.employee_id === value?.employee_id}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        label="重み"
                        value={tempManagerWeight}
                        onChange={(e) => setTempManagerWeight(parseFloat(e.target.value) || 0)}
                        inputProps={{ step: 0.1, min: 0, max: 1 }}
                        helperText={`${Number(tempManagerWeight * 100).toFixed(0)}%`}
                        fullWidth
                        margin="none"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="contained"
                        onClick={handleAddManagerEvaluator}
                        startIcon={<AddIcon />}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        追加
                      </Button>
                    </Grid>
                  </Grid>

                  {managerEvaluators.length > 0 && (
                    <Paper sx={{ mt: 2, p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        追加済み評価者 (合計: {Number(calculateTotalWeight(managerEvaluators) * 100).toFixed(1)}%)
                      </Typography>
                      <List dense>
                        {managerEvaluators.map((evaluator, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${evaluator.employee.name} (${evaluator.employee.employee_id})`}
                              secondary={`重み: ${Number(evaluator.weight * 100).toFixed(0)}%`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton edge="end" onClick={() => handleRemoveManagerEvaluator(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                      {Math.abs(calculateTotalWeight(managerEvaluators) - 1.0) > 0.001 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          重みの合計が100%になっていません
                        </Alert>
                      )}
                    </Paper>
                  )}
                </>
              )}

              {disableManagerEval && (
                <Alert severity="warning">
                  上長評価が無効です。部門長評価が上長評価の役割も担います。
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 部門長評価者セクション */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>部門長評価者</Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                部門長評価は1人のみ指定できます。定量評価の性質上、複数人での評価はできません。
              </Alert>

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={7}>
                  <Autocomplete
                    options={employees || []}
                    getOptionLabel={(option) => `${option.name} (${option.employee_id})`}
                    value={tempDeptEvaluator}
                    onChange={(_, newValue) => setTempDeptEvaluator(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="評価者を選択" margin="none" />
                    )}
                    isOptionEqualToValue={(option, value) => option.employee_id === value?.employee_id}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="重み"
                    value={tempDeptWeight}
                    onChange={(e) => setTempDeptWeight(parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.1, min: 0, max: 1 }}
                    helperText={`${Number(tempDeptWeight * 100).toFixed(0)}%`}
                    fullWidth
                    margin="none"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    sx={{ mt: 1 }}
                    onClick={handleAddDeptEvaluator}
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    追加
                  </Button>
                </Grid>
              </Grid>

              {deptEvaluators.length > 0 && (
                <Paper sx={{ mt: 2, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    追加済み評価者 (合計: {Number(calculateTotalWeight(deptEvaluators) * 100).toFixed(1)}%)
                  </Typography>
                  <List dense>
                    {deptEvaluators.map((evaluator, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${evaluator.employee.name} (${evaluator.employee.employee_id})`}
                          secondary={`重み: ${Number(evaluator.weight * 100).toFixed(0)}%`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveDeptEvaluator(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  {Math.abs(calculateTotalWeight(deptEvaluators) - 1.0) > 0.001 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      重みの合計が100%になっていません
                    </Alert>
                  )}
                </Paper>
              )}
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              自己評価は自動的に作成されます（設定不要）
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIndividualDialog}>キャンセル</Button>
          <Button onClick={handleSubmitIndividualRoute} variant="contained" color="primary">
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==============================
          一括作成ダイアログ（CSVインポート）
          ============================== */}
      <Dialog open={bulkDialogOpen} onClose={() => { handleCloseBulkDialog(); setImportErrors([]); setCsvFile(null); }} maxWidth="md" fullWidth>
        <DialogTitle>評価ルート一括作成（CSVインポート）</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              CSVファイルから評価ルートを一括作成できます。
              データエクスポートでダウンロードしたCSVを編集してアップロードしてください。
            </Alert>

            {/* ステップ1: データエクスポート */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                ステップ1: 現在のデータをエクスポート
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                現在の評価ルートデータをCSVでダウンロードします。このファイルを編集してアップロードできます。
              </Typography>
              <Button
                variant="outlined"
                onClick={handleExportData}
                startIcon={<DownloadIcon />}
              >
                データエクスポート
              </Button>
            </Paper>

            {/* ステップ2: CSVアップロード */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                ステップ2: 記入済みCSVをアップロード
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                period_id, employee_id, 評価者情報を記入したCSVファイルをアップロードします。
              </Typography>

              <Box sx={{ mb: 2 }}>
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="csv-file-upload"
                  type="file"
                  onChange={handleCsvFileChange}
                />
                <label htmlFor="csv-file-upload">
                  <Button variant="outlined" component="span">
                    CSVファイルを選択
                  </Button>
                </label>
                {csvFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    選択されたファイル: {csvFile.name}
                  </Typography>
                )}
              </Box>

              {csvFile && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  注意: アップロードすると、CSVに記載された全ての評価ルートが作成されます。
                  重複するルートは自動的にスキップされます。
                </Alert>
              )}
            </Paper>

            {/* インポートエラー表示 */}
            {importErrors.length > 0 && (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  インポートエラー ({importErrors.length}件)
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>行番号</TableCell>
                        <TableCell>社員ID</TableCell>
                        <TableCell>エラー内容</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importErrors.map((err, index) => (
                        <TableRow key={index}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.employee_id}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{err.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { handleCloseBulkDialog(); setImportErrors([]); setCsvFile(null); }} disabled={csvUploading || previewing}>
            キャンセル
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleCsvPreview}
            variant="outlined"
            disabled={!csvFile || previewing || csvUploading}
            startIcon={previewing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {previewing ? 'プレビュー中...' : 'プレビュー'}
          </Button>
          <Button
            onClick={handleCsvUpload}
            variant="contained"
            color="primary"
            disabled={!csvFile || csvUploading || previewing}
            startIcon={csvUploading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {csvUploading ? 'アップロード中...' : '直接アップロード'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==============================
          CSVプレビューダイアログ
          ============================== */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>CSVプレビュー - ID検証結果</DialogTitle>
        <DialogContent>
          {previewData && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={previewData.error_count === 0 ? 'success' : 'error'} sx={{ mb: 3 }}>
                {previewData.error_count === 0
                  ? `✓ すべてのID（${previewData.total_rows}行）が正常に検証されました。アップロード可能です。`
                  : `✗ ${previewData.error_count}件のエラーが見つかりました。修正してから再アップロードしてください。`}
              </Alert>

              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>行</TableCell>
                      <TableCell>評価対象者</TableCell>
                      <TableCell>検証結果</TableCell>
                      <TableCell>評価者</TableCell>
                      <TableCell>検証結果</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.validation_results.map((result: any) => {
                      const hasError = !result.employee_exists || result.evaluator_checks.some((c: any) => !c.exists);

                      return (
                        <TableRow key={result.row} sx={{ bgcolor: hasError ? 'error.lighter' : 'inherit' }}>
                          <TableCell>{result.row}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {result.employee_display}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {result.employee_exists ? (
                              <Chip
                                label="✓ 存在"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip
                                label="✗ 見つかりません"
                                size="small"
                                color="error"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {result.evaluator_displays.map((display: string, index: number) => (
                              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                {display}
                              </Typography>
                            ))}
                          </TableCell>
                          <TableCell>
                            {result.evaluator_checks.map((check: any, index: number) => (
                              <Box key={index} sx={{ mb: 0.5 }}>
                                {check.exists ? (
                                  <Chip
                                    label="✓"
                                    size="small"
                                    color="success"
                                  />
                                ) : (
                                  <Chip
                                    label="✗"
                                    size="small"
                                    color="error"
                                  />
                                )}
                              </Box>
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            修正する
          </Button>
          {previewData && previewData.error_count === 0 && (
            <Button
              onClick={handleCsvUpload}
              variant="contained"
              color="primary"
              disabled={csvUploading}
              startIcon={csvUploading ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {csvUploading ? 'アップロード中...' : '確認してアップロード'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ==============================
          削除確認ダイアログ
          ============================== */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">評価ルート削除確認</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {checkingDeletion ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : deletionImpact ? (
            <Box sx={{ mt: 2 }}>
              {/* 削除対象情報 */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>削除対象</Typography>
                <Typography variant="body2">
                  従業員: <strong>{deletionImpact.employee_info.employee_name}</strong>
                </Typography>
                <Typography variant="body2">
                  従業員ID: <strong>{deletionImpact.employee_info.employee_id}</strong>
                </Typography>
                <Typography variant="body2">
                  評価期間: <strong>{deletionImpact.employee_info.period_id}</strong>
                </Typography>
                <Typography variant="body2">
                  削除されるルート数: <strong>{deletionImpact.route_count}件</strong>
                </Typography>
              </Paper>

              {/* 警告メッセージ（評価データが存在する場合） */}
              {deletionImpact.warning_message && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>警告</Typography>
                  <Typography variant="body2">{deletionImpact.warning_message}</Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                    本当に削除しますか？この操作は取り消せません。
                  </Typography>
                </Alert>
              )}

              {/* 評価データが存在しない場合 */}
              {!deletionImpact.warning_message && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    この従業員の評価データは存在しません。評価ルートのみが削除されます。
                  </Typography>
                </Alert>
              )}

              {/* 削除内容の詳細 */}
              <Paper sx={{ p: 2, bgcolor: 'info.lighter' }}>
                <Typography variant="subtitle2" gutterBottom>削除される内容</Typography>
                <Typography variant="body2">
                  • 評価ルート: <strong>{deletionImpact.route_count}件</strong>
                </Typography>
                {deletionImpact.has_evaluation_data && (
                  <>
                    <Typography variant="body2">
                      • 評価履歴: <strong>{deletionImpact.evaluation_data_count}件</strong>
                    </Typography>
                    <Typography variant="body2">
                      • 評価項目データ: <strong>全て</strong>
                    </Typography>
                  </>
                )}
              </Paper>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting || checkingDeletion}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? '削除中...' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationRouteManagementPage;
